import { LeisureType } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters, ADULTS_DEFAULT } from '@/app/common/urlParameters.ts';
import { getTotalNumAdults, hasChildren } from '@/utils/peopleUtils.ts';
import type { FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { filterTypeOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';

export enum TRAVELLER_TYPE {
  SOLO = 'solo',
  COUPLE = 'couple',
  GROUP = 'group',
  FAMILY = 'family',
}

export enum TRIP_STYLE {
  BEACH = 'beach',
  CITY = 'city',
}

export type SearchPersona = {
  travellerType: TRAVELLER_TYPE;
  tripStyle?: TRIP_STYLE;
};

export type SearchPersonaSource = {
  searchParams: SearchParams;
  filtersGroups: FiltersGroups;
};

const getTravellerType = (searchParams: SearchParams): TRAVELLER_TYPE => {
  if (hasChildren(searchParams[URLParameters.AGES])) return TRAVELLER_TYPE.FAMILY;

  const adults = getTotalNumAdults(String(searchParams[URLParameters.ADULTS] ?? ADULTS_DEFAULT));
  if (adults <= 1) return TRAVELLER_TYPE.SOLO;
  if (adults === 2) return TRAVELLER_TYPE.COUPLE;
  return TRAVELLER_TYPE.GROUP;
};

const getTripStyle = (leisureType?: string): TRIP_STYLE | undefined => {
  if (leisureType === LeisureType.CITY_BREAKS) return TRIP_STYLE.CITY;
  if (leisureType === LeisureType.BEACH) return TRIP_STYLE.BEACH;
  return undefined;
};

const leisureTypeOf = ({ searchParams, filtersGroups }: SearchPersonaSource): string | undefined => {
  const leisureFilter = filtersGroups
    .flatMap((group) => ('filters' in group ? group.filters : []))
    .find((filter) => filter.name === FILTER_NAME.LEISURE_TYPE);

  if (leisureFilter) return filterTypeOf(leisureFilter).serialise(leisureFilter);

  return searchParams[URLParameters.LEISURE] as string | undefined;
};

export const getSearchPersona = (source: SearchPersonaSource): SearchPersona => ({
  travellerType: getTravellerType(source.searchParams),
  tripStyle: getTripStyle(leisureTypeOf(source)),
});
